import {
  cacheKeys,
  useCurrentWorkspace,
  useCustomMutation,
  useCustomQuery,
  useUpdateAsset,
  useWorkspaceAsset,
  WorkspaceType,
} from '@gripp/shared-logic';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductAddonsAssignList } from '../../products/components/productAddonsAssignList';
import {
  AssignmentModal,
  AssignmentTab,
} from '../../shared/components/assignment/assignmentModal';
import { EditAssetRoutinesList } from './editAssetRoutinesList';

const FIND_ASSET_ROUTINES = `
  query FindAssetRoutines($assetId: String!) {
    asset {
      findAssetRoutines(assetId: $assetId) {
        routines
      }
    }
  }
`;

const ASSIGN_ROUTINES_TO_ASSET = `
  mutation Asset($input: AssignToAssetInput!) {
    asset {
      assignToAsset(input: $input) {
        message
        success
      }
    }
  }
`;

type Props = {
  assetId: string;
  title: string;
  onComplete: () => void;
  initialTab?: 'routines' | 'addons';
};

export const AssetRoutinesForm = ({
  assetId,
  title,
  onComplete,
  initialTab,
}: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const currentWorkspace = useCurrentWorkspace();
  const isOEM = currentWorkspace?.type === WorkspaceType.Oem;

  const { workspaceAsset } = useWorkspaceAsset(assetId);
  const asset = workspaceAsset?.asset;
  const productId = asset?.product?.id ?? '';
  const { updateAsset } = useUpdateAsset({ assetId });

  const { data: routinesData, isPending } = useCustomQuery({
    query: FIND_ASSET_ROUTINES,
    cacheKey: ['asset', 'routines', assetId],
    variables: { assetId },
  });
  const routineAssignments: string[] | undefined =
    routinesData?.asset?.findAssetRoutines?.routines;

  // Addons are carried on the asset entity itself, so their baseline comes from
  // the workspace asset (undefined until it loads, so the list is withheld).
  const addonAssignments = useMemo(() => {
    if (!asset) return undefined;
    return (asset.addons ?? [])
      .filter((a: unknown) => a != null)
      .map((a: unknown) =>
        typeof a === 'object' && (a as { id?: string })?.id != null
          ? (a as { id: string }).id
          : String(a)
      )
      .filter(Boolean) as string[];
  }, [asset]);

  const { mutateAsync: assignRoutinesToAsset } = useCustomMutation({
    query: ASSIGN_ROUTINES_TO_ASSET,
  });

  const tabs: AssignmentTab[] = [
    {
      key: 'routines',
      label: t('assets.routines'),
      searchPlaceholder: t('assets.routineSearchPlaceholder'),
      assignments: routineAssignments,
      onSave: async ({ selected }) => {
        await assignRoutinesToAsset(
          {
            input: {
              routineIds: selected,
              assetId,
              workspaceId: currentWorkspace?.id,
            },
          } as never,
          {
            onSettled: async () => {
              await queryClient.invalidateQueries({
                queryKey: cacheKeys('asset').all,
              });
            },
          }
        );
      },
      renderList: (p) => (
        <EditAssetRoutinesList
          workspaceId={currentWorkspace!.id}
          assetId={assetId}
          maxHeight={p.maxHeight}
          keyword={p.keyword}
          assignments={p.assignments}
          updateAssignments={p.onAssignmentsChange}
        />
      ),
    },
  ];

  if (isOEM) {
    tabs.push({
      key: 'addons',
      label: t('addOn.list.addOns'),
      searchPlaceholder: t('assets.addonsSearchPlaceholder'),
      assignments: addonAssignments,
      onSave: async ({ selected }) => {
        await (updateAsset as (input: unknown, opts: unknown) => Promise<unknown>)(
          {
            input: {
              id: assetId,
              name: asset?.name ?? '',
              addons: selected,
              groupIds: workspaceAsset?.groups?.map((g: { id: string }) => g.id) ?? [],
            },
          },
          {
            onSettled: async () => {
              await queryClient.invalidateQueries({
                queryKey: cacheKeys('workspaceAsset').all,
              });
              await queryClient.invalidateQueries({
                queryKey: cacheKeys('asset').all,
              });
            },
          }
        );
      },
      renderList: (p) => (
        <ProductAddonsAssignList
          workspaceId={currentWorkspace!.id}
          productId={productId}
          maxHeight={p.maxHeight}
          keyword={p.keyword}
          assignments={p.assignments}
          updateAssignments={p.onAssignmentsChange}
        />
      ),
    });
  }

  return (
    <AssignmentModal
      title={title}
      isLoading={isPending || !currentWorkspace?.id}
      initialTabKey={initialTab}
      onComplete={onComplete}
      tabs={tabs}
    />
  );
};
